import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "@/hooks/useTranslation";
import { formatDateTimeNumeric, userInitials } from "@/lib/format-amount";
import { legacyBasicFrequency } from "@/lib/legacy-basic-price";
import { cn } from "@/lib/utils";
import {
  Nullable,
  TeamBillingPriceDTO,
  TeamSubscriptionHistoryEntryDTO,
  UserDTO,
  UserHistoryAction,
} from "@shaarp/common";
import {
  ArrowLeftRightIcon,
  HistoryIcon,
  LayersIcon,
  MailIcon,
  RefreshCwIcon,
  SettingsIcon,
  UserMinusIcon,
  UserPlusIcon,
  XCircleIcon,
} from "lucide-react";

type Frequency = "monthly" | "annual";

const userLabel = (
  user: Nullable<UserDTO>,
  fallback: string,
): string => {
  if (!user) return fallback;
  return user.fullName?.trim() || user.email || fallback;
};

/** Groups actions to a Lucide icon so the timeline is scannable. */
const ACTION_ICON: Record<string, typeof HistoryIcon> = {
  UPDATE_LICENSE_QUANTITIES: LayersIcon,
  ADMIN_LICENSE_OVERRIDE: SettingsIcon,
  UPDATE_TEAM_MINIMUM_PRO_LICENSE: SettingsIcon,
  UPDATE_TEAM_INITIAL_LICENSES: SettingsIcon,
  UPDATE_BILLING: SettingsIcon,
  UPDATE_BILLING_SYNC: RefreshCwIcon,
  CANCEL_LICENSE_SUBSCRIPTION: XCircleIcon,
  SUBSCRIPTION_SCHEDULE_CANCELED_EXTERNALLY: XCircleIcon,
  CANCEL_TEAM_INVITATION: XCircleIcon,
  UPDATE_BILLING_CANCEL: XCircleIcon,
  RESUME_LICENSE_SUBSCRIPTION: RefreshCwIcon,
  UPDATE_BILLING_RESUME: RefreshCwIcon,
  FREQUENCY_SWITCH_INITIATED: ArrowLeftRightIcon,
  USER_LICENSE_CHANGE: ArrowLeftRightIcon,
  BULK_LICENSE_CONVERSION: ArrowLeftRightIcon,
  ASSIGN_LICENSE: UserPlusIcon,
  ADD_TEAM_USER: UserPlusIcon,
  USER_ACCEPT_TEAM_INVITATION: UserPlusIcon,
  UNASSIGN_LICENSE: UserMinusIcon,
  DELETE_TEAM_USER: UserMinusIcon,
  SEND_TEAM_INVITATION: MailIcon,
  SEND_TEAM_INVITATION_BILLING: MailIcon,
};

interface CardSubscriptionHistoryProps {
  entries: TeamSubscriptionHistoryEntryDTO[];
  prices: TeamBillingPriceDTO[];
  loading?: boolean;
}

/**
 * Scrollable audit timeline of subscription actions (license add/remove, plan
 * frequency switch, seat assignment, membership changes) shown beneath the
 * license inventory in the admin modal.
 */
export const CardSubscriptionHistory = ({
  entries,
  prices,
  loading = false,
}: CardSubscriptionHistoryProps) => {
  const { t, i18n } = useTranslation();

  const frequencyLabel = (frequency: Frequency): string =>
    t(`admin.dataTableAdminTeams.dialogs.licenseInventory.history.frequency.${frequency}`);

  /** Human-readable "Pro · Monthly" label for a Stripe price id. */
  const priceLabel = (priceId: Nullable<string>): string => {
    if (!priceId) return t("admin.dataTableAdminTeams.dialogs.licenseInventory.history.unknownPlan");
    const legacyFrequency = legacyBasicFrequency(priceId);
    if (legacyFrequency) {
      const basicTier = t("admin.dataTableAdminTeams.dialogs.licenseInventory.history.tier.basic");
      return `${basicTier} · ${frequencyLabel(legacyFrequency)}`;
    }
    const price = prices.find((p) => p.id === priceId);
    if (!price) return t("admin.dataTableAdminTeams.dialogs.licenseInventory.history.unknownPlan");
    const tier = t("admin.dataTableAdminTeams.dialogs.licenseInventory.history.tier.professional");
    const freq = frequencyLabel(price.interval === "year" ? "annual" : "monthly");
    return `${tier} · ${freq}`;
  };

  const actionLabel = (action: UserHistoryAction): string =>
    t(`admin.dataTableAdminTeams.dialogs.licenseInventory.history.action.${action}`, {
      defaultValue: action,
    });

  /** Builds a compact, human-readable detail line per action type. */
  const detailFor = (
    entry: TeamSubscriptionHistoryEntryDTO,
  ): Nullable<string> => {
    const data = entry.data as Record<string, any>;
    const target = userLabel(
      entry.targetUser,
      t("admin.dataTableAdminTeams.dialogs.licenseInventory.history.someone"),
    );

    switch (entry.action) {
      case "UPDATE_LICENSE_QUANTITIES": {
        const parts: string[] = [];
        // History rows written before the Basic plan was abandoned (#744) still
        // carry a `basic` count: keep reading them.
        for (const tier of ["pro", "basic"] as const) {
          const before = data.before?.[tier] ?? 0;
          const after = data.after?.[tier] ?? 0;
          if (before === after) continue;
          const tierLabel =
            tier === "pro"
              ? t("admin.dataTableAdminTeams.dialogs.licenseInventory.history.tier.professional")
              : t("admin.dataTableAdminTeams.dialogs.licenseInventory.history.tier.basic");
          parts.push(`${tierLabel} ${before} → ${after}`);
        }
        const scope = `${frequencyLabel(data.frequency)}`;
        return parts.length > 0 ? `${scope} · ${parts.join(", ")}` : scope;
      }
      case "CANCEL_LICENSE_SUBSCRIPTION":
        return data.effectiveAt
          ? t("admin.dataTableAdminTeams.dialogs.licenseInventory.history.effectiveOn", {
              frequency: frequencyLabel(data.frequency),
              date: formatDateTimeNumeric(data.effectiveAt, i18n.language),
            })
          : frequencyLabel(data.frequency);
      case "RESUME_LICENSE_SUBSCRIPTION":
        return frequencyLabel(data.frequency);
      case "ASSIGN_LICENSE":
        return `${target} · ${priceLabel(data.priceId)}`;
      case "UNASSIGN_LICENSE":
        return `${target} · ${priceLabel(data.previousPriceId)}`;
      case "USER_LICENSE_CHANGE":
        return `${target} · ${priceLabel(data.fromPriceId)} → ${priceLabel(
          data.toPriceId,
        )}`;
      case "BULK_LICENSE_CONVERSION":
        return t("admin.dataTableAdminTeams.dialogs.licenseInventory.history.bulkConversion", {
          count: data.affectedUserIds?.length ?? 0,
          from: priceLabel(data.fromPriceId),
          to: priceLabel(data.toPriceId),
        });
      case "ADMIN_LICENSE_OVERRIDE": {
        const base = t(
          "admin.dataTableAdminTeams.dialogs.licenseInventory.history.overrideDetail",
          {
            frequency: frequencyLabel(data.frequency),
            pro: data.proQuantity ?? 0,
          },
        );
        return data.reason ? `${base} — ${data.reason}` : base;
      }
      case "UPDATE_TEAM_INITIAL_LICENSES":
        return t(
          "admin.dataTableAdminTeams.dialogs.licenseInventory.history.initialLicensesDetail",
          {
            frequency: frequencyLabel(data.frequency),
            count: data.count ?? 0,
          },
        );
      case "FREQUENCY_SWITCH_INITIATED":
        return `${frequencyLabel(data.fromFrequency)} → ${frequencyLabel(
          data.toFrequency,
        )}`;
      case "ADD_TEAM_USER":
        return data.priceId ? priceLabel(data.priceId) : null;
      case "DELETE_TEAM_USER":
        return target;
      case "SEND_TEAM_INVITATION":
      case "SEND_TEAM_INVITATION_BILLING":
        return data.email ?? null;
      case "USER_ACCEPT_TEAM_INVITATION":
        return data.priceId ? priceLabel(data.priceId) : null;
      default:
        return null;
    }
  };

  return (
    <Card className="overflow-hidden p-0">
      <div className="space-y-1.5 px-6 pt-5 pb-4">
        <h2 className="text-base font-semibold leading-tight">
          {t("admin.dataTableAdminTeams.dialogs.licenseInventory.history.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("admin.dataTableAdminTeams.dialogs.licenseInventory.history.subtitle")}
        </p>
      </div>

      <div className="border-t">
        {loading ? (
          <div className="space-y-4 px-6 py-4">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-3 w-24" />
              </div>
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">
            {t("admin.dataTableAdminTeams.dialogs.licenseInventory.history.empty")}
          </div>
        ) : (
          <ScrollArea className="h-[60vh] max-h-[520px]">
            <ul className="divide-y">
              {entries.map((entry) => {
                const Icon = ACTION_ICON[entry.action] ?? HistoryIcon;
                const detail = detailFor(entry);
                return (
                  <li
                    key={entry.id}
                    className="flex items-start gap-3 px-6 py-3"
                  >
                    <div
                      className={cn(
                        "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                        "bg-muted text-muted-foreground",
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>

                    <div className="min-w-0 flex-1 space-y-0.5">
                      <p className="text-sm font-medium leading-tight">
                        {actionLabel(entry.action)}
                      </p>
                      {detail && (
                        <p className="truncate text-xs text-muted-foreground">
                          {detail}
                        </p>
                      )}
                      <div className="flex items-center gap-1.5 pt-0.5 text-xs text-muted-foreground">
                        <Avatar className="h-4 w-4">
                          {entry.actor?.imageUrl && (
                            <AvatarImage
                              src={entry.actor.imageUrl}
                              alt={entry.actor.fullName}
                            />
                          )}
                          <AvatarFallback className="text-[8px]">
                            {userInitials(entry.actor)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate">
                          {userLabel(
                            entry.actor,
                            t(
                              "admin.dataTableAdminTeams.dialogs.licenseInventory.history.system",
                            ),
                          )}
                        </span>
                      </div>
                    </div>

                    <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                      {formatDateTimeNumeric(entry.date, i18n.language)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}
      </div>
    </Card>
  );
};
